<template>
  <div class="unqualified-drawer-main">
    <!-- Step A: 处理方式选择 -->
    <div v-if="statusStep === 'A'" class="step-a-content">
      <div class="content">
        <!-- 处理方式选择 -->
        <div class="handle-type-section">
          <div class="handle-type-row">
            <span class="section-label">{{ $l('form_label_handle_method') || '处理方式' }}</span>
            <el-radio-group v-model="handleType" size="small">
              <el-radio-button label="C">{{ $l('btn_apply_concession') || '申请让步接受' }}</el-radio-button>
              <el-radio-button label="R">{{ $l('btn_apply_return') || '申请退货' }}</el-radio-button>
            </el-radio-group>
          </div>
        </div>

        <!-- 申请理由（仅在申请让步接受时显示） -->
        <div v-if="handleType === 'C'" class="form-section">
          <div class="section-label">
            <span class="required-star">*</span>
            <span>{{ $l('form_label_application_reason') || '申请理由' }}</span>
          </div>
          <div class="reason-input-wrapper" @click="openReasonSelector">
            <div class="reason-input-content">
              <div
                v-if="selectedReasons.length === 0"
                class="reason-placeholder"
              >
                {{ $l('form_placeholder_select_reason') || '请选择申请理由' }}
              </div>
              <div v-else class="reason-tags">
                <el-tag
                  v-for="(reason, index) in selectedReasons"
                  :key="index"
                  closable
                  size="small"
                  type="info"
                  @close="removeReason(index)"
                >
                  {{ reason.defectIssuesTitle }}
                </el-tag>
              </div>
            </div>
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>

        <!-- 备注说明 -->
        <div class="form-section">
          <div class="section-label">{{ $l('form_label_remark') || '备注说明' }}</div>
          <el-input
            v-model="applicationRemark"
            type="textarea"
            :rows="4"
            :placeholder="$l('form_placeholder_remark') || '请输入备注说明'"
            maxlength="300"
            show-word-limit
          />
        </div>
      </div>

      <!-- 底部操作按钮 -->
      <div class="footer">
        <el-button size="small" @click="handleCancel">{{ $l('btn_cancel') || '取消' }}</el-button>
        <el-button size="small" type="primary" @click="handleConfirm">{{ $l('btn_confirm') || '确定' }}</el-button>
      </div>
    </div>

    <!-- Step B: 申请理由选择 -->
    <div v-if="statusStep === 'B'" class="step-b-content">
      <div class="header">
        <el-button
          icon="el-icon-arrow-left"
          type="text"
          @click="backToStepA"
          class="back-btn"
        >
          {{ $l('btn_back') || '返回' }}
        </el-button>
        <div class="title">{{ $l('form_label_application_reason') || '申请理由' }}</div>
        <el-button type="primary" size="small" @click="confirmReasonSelection">
          {{ $l('btn_confirm_submit') || '确认填报' }}
        </el-button>
      </div>

      <div class="content">
        <el-checkbox-group v-model="tempSelectedReasonCodes">
          <div
            v-for="group in groupedReasons"
            :key="group.defectIssuesGroup"
            class="issue-group"
          >
            <div class="group-title">{{ group.defectIssuesGroup }}</div>
            <div class="group-items">
              <el-checkbox
                v-for="item in group.items"
                :key="item.defectIssuesCode"
                :label="item.defectIssuesCode"
              >
                {{ item.defectIssuesTitle }}
              </el-checkbox>
            </div>
          </div>
        </el-checkbox-group>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

export default {
  name: 'UnqualifiedDrawerMain',
  props: {
    inspectionCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      statusStep: 'A', // A: 处理方式选择, B: 申请理由选择
      handleType: 'C', // C: 申请让步接受, R: 申请退货
      selectedReasons: [],
      applicationRemark: '',
      reasonsList: [],
      tempSelectedReasonCodes: []
    }
  },
  computed: {
    groupedReasons() {
      if (lodash.isEmpty(this.reasonsList)) return []

      const grouped = lodash.groupBy(this.reasonsList, 'defectIssuesGroup')

      return Object.entries(grouped).map(([defectIssuesGroup, items]) => ({
        defectIssuesGroup,
        items
      }))
    }
  },
  watch: {
    statusStep(newVal) {
      this.$emit('update:status-step', newVal)
    }
  },
  methods: {
    async openReasonSelector() {
      // 切换到 Step B 并加载申请理由列表
      this.statusStep = 'B'

      // 初始化临时选中状态
      this.tempSelectedReasonCodes = this.selectedReasons.map(
        (r) => r.defectIssuesCode
      )

      // 加载理由列表
      await this.loadReasonsList()
    },
    async loadReasonsList() {
      try {
        const res = await this.$api.inspectionDefectIssuesListApi({
          issueType: 'C'
        })

        if (res.status === 200 && res.body?.data) {
          this.reasonsList = res.body.data
        }
      } catch (error) {
        this.$message.error(error.message || this.$l('tips_load_reason_failed') || '加载申请理由失败')
      }
    },
    backToStepA() {
      this.statusStep = 'A'
      this.tempSelectedReasonCodes = []
    },
    confirmReasonSelection() {
      if (lodash.isEmpty(this.tempSelectedReasonCodes)) {
        this.$message.warning(this.$l('tips_select_reason') || '请选择申请理由')
        return
      }

      // 根据选中的 code 过滤出完整的理由信息
      this.selectedReasons = this.reasonsList
        .filter((item) =>
          this.tempSelectedReasonCodes.includes(item.defectIssuesCode)
        )
        .map((item) => ({
          defectIssuesCode: item.defectIssuesCode,
          defectIssuesTitle: item.defectIssuesTitle
        }))

      // 返回 Step A
      this.backToStepA()
    },
    removeReason(index) {
      this.selectedReasons.splice(index, 1)
    },
    handleCancel() {
      this.$emit('close')
    },
    handleConfirm() {
      if (this.handleType === 'C') {
        // 申请让步接受，需要申请理由
        if (this.selectedReasons.length === 0) {
          this.$message.warning(this.$l('tips_select_reason') || '请选择申请理由')
          return
        }

        // 构建 inspectionDefect 参数
        const inspectionDefect = {
          itemList: this.selectedReasons.map((reason) => ({
            inspectionCode: this.inspectionCode || '',
            defectIssuesCode: reason.defectIssuesCode,
            quantity: 1,
            issueType: 'C'
          }))
        }

        this.$emit('confirm', {
          status: 'C',
          applicationRemark: this.applicationRemark,
          inspectionDefect
        })
      } else if (this.handleType === 'R') {
        // 申请退货
        this.$emit('confirm', {
          status: 'R',
          applicationRemark: this.applicationRemark
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.unqualified-drawer-main {
  display: flex;
  flex-direction: column;
  min-height: 400px;

  .step-a-content,
  .step-b-content {
    display: flex;
    flex-direction: column;
    min-height: 400px;
  }

  .content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
  }

  // Step A 样式
  .step-a-content {
    .handle-type-section {
      margin-bottom: 24px;
    }

    .handle-type-row {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .form-section {
      margin-bottom: 24px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .section-label {
      font-size: 14px;
      font-weight: 500;
      color: #101010;

      &:not(.handle-type-row .section-label) {
        margin-bottom: 12px;
      }

      .required-star {
        color: #f56c6c;
        margin-right: 4px;
      }
    }

    .reason-input-wrapper {
      min-height: 40px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      padding: 8px 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      transition: border-color 0.3s;

      &:hover {
        border-color: #409eff;
      }

      .reason-input-content {
        flex: 1;
        min-height: 24px;

        .reason-placeholder {
          color: #c0c4cc;
          font-size: 14px;
        }

        .reason-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;

          .el-tag {
            margin: 0;
          }
        }
      }

      .el-icon-arrow-right {
        color: #909399;
        font-size: 14px;
        margin-left: 8px;
      }
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 16px 20px;
      border-top: 1px solid #e8e8e8;
      background-color: #fff;
    }
  }

  // Step B 样式
  .step-b-content {
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #e8e8e8;

      .back-btn {
        padding: 0;
        font-size: 14px;
        color: #606266;

        &:hover {
          color: #409eff;
        }
      }

      .title {
        flex: 1;
        text-align: center;
        font-size: 16px;
        font-weight: 600;
        color: #071437;
      }
    }

    .content {
      .issue-group {
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }

        .group-title {
          font-size: 14px;
          font-weight: 600;
          color: #101010;
          margin-bottom: 12px;
        }

        .group-items {
          display: flex;
          flex-wrap: wrap;
          gap: 12px 20px;

          :deep(.el-checkbox) {
            margin-right: 0;

            .el-checkbox__label {
              font-size: 13px;
              color: #333;
            }
          }
        }
      }
    }
  }
}
</style>
